/**
 * Pilotage Planner — pure geometry helpers for the building tools. No React, no
 * map: just the maths the plan layer and the toolbar lean on, so they can be
 * unit-tested. Covers per-leg time-to-go labels (from one base speed), a screen-
 * space anti-overlap pass so short-leg labels don't stack, and slicing a tapped
 * depth contour between two points for "follow as route" / "clearing depth".
 */
import { rhumbDistanceNm } from '@/nav';
import type { LonLat, PlanWaypoint } from './plannerModel';

export interface LegTimeLabel {
  /** The leg midpoint, where the label sits, [lon, lat]. */
  position: LonLat;
  text: string;
  minutes: number;
  distanceNm: number;
}

/** Format a leg time-to-go, e.g. "8 min" or "1 h 05". */
export function formatTtg(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h} h ${String(rem).padStart(2, '0')}`;
}

/**
 * Per-leg time-to-go labels at each leg midpoint, worked out from the plan's one
 * base speed (distance ÷ speed). A stopped/zero speed is floored to a whisker so
 * we still show a (large) time rather than dividing by zero.
 */
export function legTimeLabels(waypoints: PlanWaypoint[], speedKn: number): LegTimeLabel[] {
  const out: LegTimeLabel[] = [];
  const spd = speedKn > 0 ? speedKn : 0.1;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i].position;
    const b = waypoints[i + 1].position;
    const d = rhumbDistanceNm(a, b);
    const minutes = (d / spd) * 60;
    out.push({
      position: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      text: formatTtg(minutes),
      minutes,
      distanceNm: d,
    });
  }
  return out;
}

export interface Px {
  x: number;
  y: number;
}

/**
 * Nudge labels apart so short-leg labels don't sit on top of each other. Works in
 * screen pixels (fed the projected leg midpoints): walks them top to bottom and
 * pushes any that would land closer than minGapPx down until they clear, keeping
 * their original order. Returns a new array aligned to the input order, so the
 * layer can unproject the adjusted points back to the chart.
 */
export function spreadLabels(points: Px[], minGapPx = 20): Px[] {
  const order = points.map((p, i) => ({ p, i })).sort((u, v) => u.p.y - v.p.y);
  const out: Px[] = points.map((p) => ({ x: p.x, y: p.y }));
  let lastY = -Infinity;
  for (const { p, i } of order) {
    let y = p.y;
    if (y - lastY < minGapPx) y = lastY + minGapPx;
    out[i] = { x: p.x, y };
    lastY = y;
  }
  return out;
}

/** The index of the path vertex nearest to p (great-circle-ish, fine at this scale). */
export function nearestVertexIndex(path: LonLat[], p: LonLat): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < path.length; i++) {
    const d = rhumbDistanceNm(path[i], p);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * The contour sub-path between the vertices nearest a and b (inclusive), oriented
 * from a toward b, so "follow as route" runs the way the user tapped. Returns the
 * whole path if it's too short to slice.
 */
export function sliceContourBetween(path: LonLat[], a: LonLat, b: LonLat): LonLat[] {
  if (path.length < 2) return path.slice();
  const ia = nearestVertexIndex(path, a);
  const ib = nearestVertexIndex(path, b);
  const lo = Math.min(ia, ib);
  const hi = Math.max(ia, ib);
  const seg = path.slice(lo, hi + 1);
  return ia <= ib ? seg : seg.reverse();
}
